<template>
  <v-card class="elevation-12" :loading="loading">
    <v-toolbar
        color="primary"
        dark
        flat
    >
      <v-toolbar-title>{{ $t('editOrganizationForm.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>
    </v-toolbar>
    <v-card-text>
      <v-form class="pt-5">
        <v-text-field
            :label="$t('editOrganizationForm.labels.tin')"
            v-model="form.inn"
            type="text"
            maxlength="9"
            :counter="9"
            :readonly="true"
        />

        <v-text-field
            v-model="form.pinfl"
            :label="$t('editOrganizationForm.labels.pinfl')"
            type="text"
            maxlength="..."
        ></v-text-field>

        <v-text-field
            v-model="form.name"
            :label="$t('editOrganizationForm.labels.name')"
            type="text"
            maxlength="400"
            :counter="400"
            :error-messages="nameErrors"
        />

        <v-text-field
            v-model="form.address"
            :label="$t('editOrganizationForm.labels.address')"
            type="text"
            maxlength="400"
            :counter="400"
            :error-messages="addressErrors"
        />

        <v-text-field
            v-model="form.accountNumber"
            :label="$t('editOrganizationForm.labels.accountNumber')"
            type="text"
            maxlength="30"
            :counter="30"
            :error-messages="addressErrors"
            :disabled="isFormSuccess"
        />

        <v-text-field
            v-model="form.mfo"
            :label="$t('editOrganizationForm.labels.mfo')"
            type="text"
            maxlength="5"
            :counter="400"
            :error-messages="addressErrors"
            :disabled="isFormSuccess"
        />

        <v-text-field
            v-model="form.phoneNumber"
            :label="$t('editOrganizationForm.labels.phoneNumber')"
            type="text"
            maxlength="12"
            :counter="12"
            :error-messages="phoneNumberErrors"
            :disabled="isFormSuccess"
        />

        <v-text-field
            v-model="form.vatRegCode"
            :label="$t('editOrganizationForm.labels.vatCode')"
            type="text"
            maxlength="..."
        ></v-text-field>

        <v-text-field
            v-model="form.contractSum"
            :label="$t('editOrganizationForm.labels.contractSum')"
            type="number"
            step="0.01"
        ></v-text-field>

        <v-text-field
            v-model="form.sumForMail"
            :label="$t('editOrganizationForm.labels.sumForMail')"
            type="number"
            step="0.01"
        ></v-text-field>

        <v-text-field
            v-model="form.sumForOverPaged"
            :label="$t('editOrganizationForm.labels.sumForOverPaged')"
            type="number"
            step="0.01"
        ></v-text-field>


        <v-text-field
            v-model="form.contractNumber"
            :label="$t('editOrganizationForm.labels.contractNumber')"
            type="text"
            maxlength="400"
            :counter="400"
        />

        <v-spacer class="py-2"></v-spacer>

        <DateInput v-model="form.contractDateFrom"
                   :label="$t('editOrganizationForm.labels.contractDateFrom')"
                   :dense="true"
                   min-width="0"
                   max-width="80%"
        />

        <v-spacer class="py-2"></v-spacer>

        <DateInput v-model="form.contractDateTo"
                   :label="$t('editOrganizationForm.labels.contractDateTo')"
                   :dense="true"
                   min-width="0"
                   max-width="80%"
        />

        <v-spacer class="py-2"></v-spacer>

        <v-responsive>
          <v-row>
            <v-col cols="12" class="py-2">
              <v-autocomplete v-model="form.contractSignerUnitId"
                                :items="units"
                                :label="$t('editOrganizationForm.labels.contractSignerBranch')"
                                :dense="true"
                                :clearable="true"
                                item-text="name"
                                item-value="id"/>
            </v-col>
            <v-spacer/>
          </v-row>
        </v-responsive>

        <v-responsive>
          <v-row>
            <v-col cols="10">
              <v-autocomplete v-model="selectedBranch"
                                :items="branches"
                                :label="$t('editOrganizationForm.labels.roamingBranches')"
                                :dense="true"
                                :clearable="true"
                                item-text="branchName"
                                item-value="branchCode"/>
            </v-col>
            <v-col cols="2" class="pl-10">
              <v-btn icon color="primary" @click="loadBranches">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-col>
            <p class="pl-3" style="color: #ff0e0e" v-if="showInnRequiredMessage">{{ $t('editOrganizationForm.messages.tinRequired') }}</p>
            <v-spacer/>
          </v-row>
        </v-responsive>

        <v-checkbox
            v-model="form.hasContractWithGlobalUnit"
            :label="$t('editOrganizationForm.checkboxes.hasGlobalContract')"
        ></v-checkbox>

        <v-checkbox
            v-model="form.isLtd"
            :label="$t('editOrganizationForm.checkboxes.isIndividual')"
        ></v-checkbox>

        <v-radio-group v-model="form.isActive">
          <template v-slot:label>
            <div><strong>{{ $t('editOrganizationForm.status.title') }}</strong></div>
          </template>
          <v-radio :value="true" color="success">
            <template v-slot:label>
              <div>{{ $t('editOrganizationForm.status.active') }}</div>
            </template>
          </v-radio>
          <v-radio :value="false" color="secondary">
            <template v-slot:label>
              <div>{{ $t('editOrganizationForm.status.inactive') }}</div>
            </template>
          </v-radio>
        </v-radio-group>
        <v-snackbar v-model="isServerError"
                    :timeout="5000"
                    :top="true"
                    color="error"
        >
          {{ $t('editOrganizationForm.messages.serverError') }}
        </v-snackbar>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn text color="secondary" @click="onCancelButtonClicked">{{ $t('editOrganizationForm.actions.cancel') }}</v-btn>
      <v-spacer/>
      <v-btn text color="primary" @click="onSaveButtonClicked">{{ $t('editOrganizationForm.actions.save') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {maxLength, minLength, numeric, required} from "vuelidate/lib/validators";
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP} from "@/store/modules/layout/actions";
import OrganizationService from "@/services/organization.service";
import _ from "underscore";
import Mappers from "@/mappers";
import DateInput from "@/components/input/DateInput";
import BranchService from "@/services/branch.service";
import GlobalUnitService from "@/services/global.unit.service";

export default {
  name: "EditOrganizationComponent",
  components: {
    DateInput
  },
  data() {
    return {
      loading: false,
      units: [],
      branches: [],
      selectedBranch: '',
      showInnRequiredMessage: false,

      form: {
        inn: '',
        name: '',
        address: '',
        accountNumber: '',
        mfo: '',
        phoneNumber: '',
        branch: {
          branchName: '',
          branchCode: ''
        },
        vatRegCode: '',
        pinfl: '',
        isLtd: false,
        contractSum: null,
        sumForMail: null,
        sumForOverPaged: null,

        contractNumber: '',
        contractDateFrom: null,
        contractDateTo: null,
        contractSignerUnitId: '',
        isActive: false,
        hasContractWithGlobalUnit: false
      },

      isFormSuccess: false,
      isServerError: false
    }
  },
  computed: {
    nameErrors() {
      const errors = []

      if (this.$v.form.name.$dirty) {
        if (!this.$v.form.name.required) {
          errors.push(this.$t('editOrganizationForm.errors.fullNameRequired'))
        } else {
          !this.$v.form.name.maxLength && errors.push(this.$t('editOrganizationForm.errors.fullNameTooLong'))
        }
      }

      return errors
    },
    addressErrors() {
      const errors = []

      if (this.$v.form.address.$dirty) {
        if (!this.$v.form.address.required) {
          errors.push(this.$t('editOrganizationForm.errors.addressRequired'))
        } else {
          !this.$v.form.name.maxLength && errors.push(this.$t('editOrganizationForm.errors.addressTooLong'))
        }
      }

      return errors
    },
    accountNumberErrors() {
      const errors = []

      if (this.$v.form.accountNumber.$dirty) {
        if (!this.$v.form.accountNumber.required) {
          errors.push(this.$t('editOrganizationForm.errors.accountRequired'))
        } else {
          !this.$v.form.address.maxLength && errors.push(this.$t('editOrganizationForm.errors.accountTooLong'))
        }
      }

      return errors
    },
    mfoErrors() {
      const errors = []

      if (this.$v.form.mfo.$dirty) {
        if (!this.$v.form.mfo.required) {
          errors.push(this.$t('editOrganizationForm.errors.mfoRequired'))
        } else {
          !this.$v.form.address.maxLength && errors.push(this.$t('editOrganizationForm.errors.mfoTooLong'))
        }
      }

      return errors
    },
    phoneNumberErrors() {
      const errors = []
      if (this.$v.form.phoneNumber.$dirty) {
        if (!this.$v.form.phoneNumber.required) {
          errors.push(this.$t('editOrganizationForm.errors.phoneRequired'))
        } else {
          if (!this.$v.form.phoneNumber.numeric || !this.$v.form.phoneNumber.format)
            errors.push(this.$t('editOrganizationForm.errors.phoneFormat'))
          if (!this.$v.form.phoneNumber.minLength || !this.$v.form.phoneNumber.maxLength)
            errors.push(this.$t('editOrganizationForm.errors.phoneLength'))
        }
      }

      return errors
    },
  },
  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(400)
      },
      address: {
        required,
        maxLength: maxLength(400)
      },
      mfo: {
        maxLength: maxLength(5)
      },
      accountNumber: {
        maxLength: maxLength(30)
      },
      phoneNumber: {
        required,
        maxLength: maxLength(12),
        minLength: minLength(12),
        numeric,
        format(value) {
          return /^998\d{9}$/.test(value);
        }
      },
    }
  },
  methods: {
    loadUnits() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      GlobalUnitService.getUnits({inn: this.$store.getters.inn})
          .then(response => {
            this.units = _.map(response.data, o => {
              return Mappers.mapUnit(o)
            })
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    loadBranches() {
      if (this.form.inn.length === 9) {
        this.$store.dispatch(LAYOUT_LOADING_START)

        BranchService.getBranches({inn: this.form.companyInn})
            .then(response => {
              this.branches = _.map(response.data, b => {
                return Mappers.mapBranch(b)
              })
            })
            .finally(() => {
              this.$store.dispatch(LAYOUT_LOADING_STOP)
            })
      } else {
        this.showInnRequiredMessage = true;
      }
    },
    set(organization) {
      this.clear()

      this.form.inn = organization.inn
      this.form.name = organization.name
      this.form.address = organization.address
      this.form.accountNumber = organization.accountNumber
      this.form.mfo = organization.mfo
      this.form.vatRegCode = organization.vatRegCode

      // load branch
      {
        this.form.branch = {
          branchCode: organization.branchCode,
          branchName: organization.branchName
        }

        this.branches = [this.form.branch]
        this.selectedBranch = this.form.branch.branchCode
      }

      this.form.isLtd = organization.isLtd
      this.form.pinfl = organization.pinfl
      this.form.contractSum = organization.contractSum
      this.form.sumForMail = organization.sumForMail
      this.form.sumForOverPaged = organization.sumForOverPaged
      this.form.phoneNumber = organization.phoneNumber
      this.form.contractNumber = organization.contractNumber
      this.form.contractDateFrom = organization.contractDateFrom
      this.form.contractDateTo = organization.contractDateTo
      this.form.contractSignerUnitId = organization.contractSignerUnitId
      this.form.isActive = organization.isActive
      this.form.hasContractWithGlobalUnit = organization.hasContractWithGlobalUnit

      this.loadUnits()
    },
    clear() {
      this.$v.$reset()
      this.form.inn = ''
      this.form.name = ''
      this.form.address = ''
      this.form.accountNumber = ''
      this.form.mfo = ''
      this.form.phoneNumber = ''
      this.form.branch.branchName = ''
      this.form.branch.branchCode = ''
      this.form.vatRegCode = ''
      this.form.pinfl = ''
      this.form.isLtd = false
      this.form.hasContractWithGlobalUnit = false
      this.form.contractSum = null
      this.form.sumForMail = null
      this.form.sumForOverPaged = null

      this.form.contractNumber = ''
      this.form.contractDateFrom = null
      this.form.contractDateTo = null
      this.form.contractSignerUnitId = ''

      this.form.isActive = false

      this.form.isInnExists = false
      this.form.isUsernameExists = false
      this.form.isOrganizationNameExists = false

      this.branches = []
    },

    onCancelButtonClicked() {
      this.clear()
      this.$emit('cancel')
    },
    onSaveButtonClicked() {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        this.loading = true;
        this.$store.dispatch(LAYOUT_LOADING_START)

        this.isServerError = false

        console.log(this.form.contractDateFrom)
        OrganizationService.editOrganization({
          inn: this.form.inn,
          name: this.form.name,
          address: this.form.address,
          accountNumber: this.form.accountNumber,
          vatRegCode: this.form.vatRegCode,
          branchCode: this.form.branch.branchCode,
          branchName: this.form.branch.branchName,
          isLtd: this.form.isLtd,
          pinfl: this.form.pinfl,
          contractSum: this.form.contractSum,
          sumForMail: this.form.sumForMail,
          sumForOverPaged: this.form.sumForOverPaged,
          mfo: this.form.mfo,
          phoneNumber: this.form.phoneNumber,
          contractNumber: this.form.contractNumber,
          contractDateFrom: this.form.contractDateFrom,
          contractDateTo: this.form.contractDateTo,
          contractSignerUnitId: this.form.contractSignerUnitId,
          isActive: this.form.isActive,
          hasContractWithGlobalUnit: this.form.hasContractWithGlobalUnit
        })
            .then(() => {
              this.clear()
              this.$emit('updated')
            }, () => {
              this.isServerError = true
            })
            .finally(() => {
              this.loading = false
              this.$store.dispatch(LAYOUT_LOADING_STOP)
            })
      }
    }
  },
  watch: {
    selectedBranch(branchCode) {
      let branch = this.branches.find(item => item.branchCode === branchCode);

      this.form.branch.branchName = branch.branchName;
      this.form.branch.branchCode = branch.branchCode;
    },
    'form.inn'(newInnValue) {
      this.showInnRequiredMessage = false;
      this.form.companyInn = newInnValue;
    },
  },
}
</script>

<style scoped>

</style>